/**
 * Corners of a receipt in its photo, as a person corrects them. Coordinates are pixels of the
 * upright original - the same space services/ai detects in, so they go back unchanged.
 */
import type { ScanCorner, ScanPage } from "@/lib/api/documents";

export const CORNER_NAMES = ["topLeft", "topRight", "bottomRight", "bottomLeft"] as const;

/** What the editor starts from: the corners used for the scan, or the whole photo. */
export function startCorners(page: ScanPage): ScanCorner[] {
  if (page.corners && page.corners.length === 4) return page.corners.map((p) => ({ ...p }));
  const w = page.sourceWidth - 1;
  const h = page.sourceHeight - 1;
  return [
    { x: 0, y: 0 },
    { x: w, y: 0 },
    { x: w, y: h },
    { x: 0, y: h },
  ];
}

const clamp = (value: number, max: number) => Math.min(Math.max(value, 0), max);

/** Moves one corner, kept inside the photo. */
export function moveCorner(
  corners: ScanCorner[],
  index: number,
  to: ScanCorner,
  page: Pick<ScanPage, "sourceWidth" | "sourceHeight">,
): ScanCorner[] {
  return corners.map((corner, at) =>
    at === index
      ? {
          x: Math.round(clamp(to.x, page.sourceWidth - 1)),
          y: Math.round(clamp(to.y, page.sourceHeight - 1)),
        }
      : corner,
  );
}

/** Where a pointer is in photo pixels, given where the photo is drawn on screen. */
export function pointerToPhoto(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number; width: number; height: number },
  page: Pick<ScanPage, "sourceWidth" | "sourceHeight">,
): ScanCorner {
  return {
    x: ((clientX - rect.left) / rect.width) * page.sourceWidth,
    y: ((clientY - rect.top) / rect.height) * page.sourceHeight,
  };
}

/** Arrow keys move a corner by half a percent of the photo, with Shift by five. */
export function nudge(
  corners: ScanCorner[],
  index: number,
  key: string,
  large: boolean,
  page: Pick<ScanPage, "sourceWidth" | "sourceHeight">,
): ScanCorner[] | null {
  const step = (large ? 0.05 : 0.005) * Math.max(page.sourceWidth, page.sourceHeight);
  const delta: Record<string, [number, number]> = {
    ArrowLeft: [-step, 0],
    ArrowRight: [step, 0],
    ArrowUp: [0, -step],
    ArrowDown: [0, step],
  };
  const move = delta[key];
  if (!move) return null;
  const corner = corners[index]!;
  return moveCorner(corners, index, { x: corner.x + move[0], y: corner.y + move[1] }, page);
}

/** How a page was cropped, for the editor to say so. */
export function cropState(page: ScanPage): "detected" | "manual" | "whole" {
  if (!page.corners) return "whole";
  return page.detected ? "detected" : "manual";
}
